<template>
  <v-dialog v-model="dialogVisible" max-width="800px" persistent scrollable>
    <videon-card :heading="formTitle" show-close @close="close">
      <v-card-text>
        <v-form @submit.prevent="save">
          <v-autocomplete
            v-if="!editingEvent && scope !== 'device'"
            v-model="form.device_guid"
            :items="deviceChoices"
            item-text="text"
            item-value="value"
            label="Device"
            outlined
            class="mb-2"
          />
          <v-text-field v-model="form.name" label="Event name" outlined required />
          <v-text-field v-model="form.location" label="Location" outlined />

          <div class="text-subtitle-2 mt-2 mb-1">Schedule</div>
          <v-row dense>
            <v-col cols="12" sm="4">
              <v-text-field
                v-model="form.startDate"
                label="Date"
                type="date"
                outlined
                dense
                hide-details="auto"
              />
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field
                v-if="startTimeManual"
                ref="startTimeManualInput"
                v-model="startTimeEditText"
                label="Start time"
                outlined
                dense
                hide-details="auto"
                placeholder="e.g. 2:30 PM"
                hint="Same 12-hour style as the preset list"
                persistent-hint
                @blur="commitStartTimeManual"
                @keydown.enter="commitStartTimeManual"
              />
              <v-text-field
                v-else
                :value="formatTimeDisplay(form.startTime)"
                label="Start time"
                outlined
                dense
                readonly
                hide-details="auto"
                hint="Double-click to type; ▼ for presets"
                persistent-hint
                @dblclick.native.stop.prevent="enableStartTimeManual"
              >
                <template #append>
                  <v-menu offset-y left>
                    <template #activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        aria-label="Choose start time"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-icon>arrow_drop_down</v-icon>
                      </v-btn>
                    </template>
                    <v-list dense max-height="280" class="scheduler-time-menu">
                      <v-list-item
                        v-for="item in timeSlotItems"
                        :key="'start-' + item.value"
                        @click="selectStartTimeSlot(item.value)"
                      >
                        <v-list-item-title>{{ item.text }}</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field
                v-if="endTimeManual"
                ref="endTimeManualInput"
                v-model="endTimeEditText"
                label="End time"
                outlined
                dense
                hide-details="auto"
                placeholder="e.g. 4:15 PM"
                hint="Same 12-hour style as the preset list"
                persistent-hint
                @blur="commitEndTimeManual"
                @keydown.enter="commitEndTimeManual"
              />
              <v-text-field
                v-else
                :value="formatTimeDisplay(form.endTime)"
                label="End time"
                outlined
                dense
                readonly
                hide-details="auto"
                hint="Double-click to type; ▼ for presets"
                persistent-hint
                @dblclick.native.stop.prevent="enableEndTimeManual"
              >
                <template #append>
                  <v-menu offset-y left>
                    <template #activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        aria-label="Choose end time"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-icon>arrow_drop_down</v-icon>
                      </v-btn>
                    </template>
                    <v-list dense max-height="280" class="scheduler-time-menu">
                      <v-list-item
                        v-for="item in endTimeSlotItems"
                        :key="'end-' + item.value"
                        @click="selectEndTimeSlot(item.value)"
                      >
                        <v-list-item-title>{{ item.text }}</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </template>
              </v-text-field>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-select
                v-model="form.durationMinutes"
                :items="durationSelectItems"
                label="Duration preset"
                outlined
                dense
                hide-details="auto"
                hint="Adjusts end time from start"
                persistent-hint
                @change="applyDurationPreset"
              />
            </v-col>
          </v-row>
          <p class="caption text--secondary mb-4 mt-1">
            {{ scheduleSummary }}
          </p>

          <scheduler-output-picker
            :device-guid="deviceGuid"
            v-model="form.output_ids"
            :disabled="!canEdit || saving"
          />
          <v-text-field v-model="form.notes" label="Notes" outlined textarea />
          <v-row>
            <v-col cols="6">
              <v-text-field
                v-model.number="form.pre_roll_seconds"
                label="Pre-roll (seconds)"
                type="number"
                outlined
              />
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model.number="form.post_roll_seconds"
                label="Post-roll (seconds)"
                type="number"
                outlined
              />
            </v-col>
          </v-row>
          <v-select
            v-model="form.recurrence.frequency"
            :items="recurrenceOptions"
            label="Recurrence"
            outlined
          />
          <v-text-field
            v-if="showRecurrenceEndDate"
            v-model="form.recurrenceEndDate"
            label="Series end date"
            type="date"
            outlined
            dense
            class="mt-2"
            :min="form.startDate"
            hide-details="auto"
            hint="Last day the series runs. Defaults: daily 1 month, weekly 3 months, monthly 1 year from start."
            persistent-hint
            @change="recurrenceEndDateTouched = true"
          />
          <div class="text-right mt-4">
            <v-btn color="secondary" text @click="close">Cancel</v-btn>
            <v-btn color="primary" class="ml-2" :loading="saving" @click="onSaveClick">
              Save
            </v-btn>
          </div>
        </v-form>
      </v-card-text>
    </videon-card>

    <v-dialog v-model="editScopeDialog" max-width="480" persistent>
      <v-card>
        <v-card-title class="headline">Save changes</v-card-title>
        <v-card-text>
          <p>This event is part of a recurring series. What should be updated?</p>
          <v-radio-group v-model="editScope" class="mt-2">
            <v-radio label="This occurrence only" value="this" />
            <v-radio label="This and all following occurrences" value="following" />
            <v-radio label="Entire series (all occurrences)" value="all" />
          </v-radio-group>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="editScopeDialog = false">Cancel</v-btn>
          <v-btn color="primary" text :loading="saving" @click="save">
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import moment from 'moment-timezone'
import SchedulerOutputPicker from './SchedulerOutputPicker.vue'
import {
  defaultRecurrenceEndDate,
  recurrenceEndDateFromUntil,
  recurrenceUntilFromEndDate,
} from '../../utils/schedulerRecurrence'

const DURATION_PRESETS_MINUTES = [15, 30, 45, 60, 90, 120, 180, 240, 360, 480]

export default {
  name: 'SchedulerEventForm',

  components: { SchedulerOutputPicker },

  props: {
    value: Boolean,
    event: { type: Object, default: null },
    initialSchedule: { type: Object, default: null },
    scope: String,
    orgGuid: String,
    fleetGuid: String,
    defaultDeviceGuid: String,
    canEdit: Boolean,
  },

  data() {
    return {
      saving: false,
      editScopeDialog: false,
      editScope: 'this',
      startTimeManual: false,
      endTimeManual: false,
      startTimeEditText: '',
      endTimeEditText: '',
      form: this.emptyForm(),
      recurrenceEndDateTouched: false,
      deviceChoices: [],
      recurrenceOptions: [
        { text: 'One time', value: 'none' },
        { text: 'Daily', value: 'daily' },
        { text: 'Weekly', value: 'weekly' },
        { text: 'Monthly', value: 'monthly' },
      ],
    }
  },

  computed: {
    dialogVisible: {
      get() { return this.value },
      set(v) { this.$emit('input', v) },
    },
    editingEvent() {
      return this.event && this.event.start_time
    },

    isSeriesEdit() {
      if (!this.editingEvent) {
        return false
      }
      if (this.event.series_id) {
        return true
      }
      const r = this.event.recurrence
      return r && r.frequency && r.frequency !== 'none'
    },

    patchStartTime() {
      return this.event._occurrenceStart || this.event.start_time
    },
    formTitle() {
      return this.editingEvent ? 'Edit Event' : 'New Event'
    },
    deviceGuid() {
      return this.editingEvent ? this.event.device_guid : this.form.device_guid
    },

    timeSlotItems() {
      return this.buildTimeSlotItems(this.form.startTime)
    },

    endTimeSlotItems() {
      return this.buildTimeSlotItems(this.form.endTime)
    },

    durationSelectItems() {
      const presets = [...DURATION_PRESETS_MINUTES]
      const current = this.form.durationMinutes
      if (current && !presets.includes(current)) {
        presets.push(current)
        presets.sort((a, b) => a - b)
      }
      return presets.map((minutes) => ({
        text: this.formatDuration(minutes),
        value: minutes,
      }))
    },

    startMoment() {
      if (!this.form.startDate || !this.form.startTime) {
        return null
      }
      const tz = this.$helpers.getDisplayTimezone()
      return moment.tz(
        this.form.startDate + ' ' + this.form.startTime,
        'YYYY-MM-DD HH:mm',
        tz
      )
    },

    endMoment() {
      if (!this.startMoment || !this.form.endTime) {
        return null
      }
      const tz = this.$helpers.getDisplayTimezone()
      let end = moment.tz(
        this.form.startDate + ' ' + this.form.endTime,
        'YYYY-MM-DD HH:mm',
        tz
      )
      if (!end.isValid()) {
        return null
      }
      if (!end.isAfter(this.startMoment)) {
        end = end.add(1, 'day')
      }
      return end
    },

    showRecurrenceEndDate() {
      const freq = this.form.recurrence && this.form.recurrence.frequency
      return freq && freq !== 'none'
    },

    recurrenceEndMoment() {
      if (!this.showRecurrenceEndDate || !this.form.recurrenceEndDate) {
        return null
      }
      const tz = this.$helpers.getDisplayTimezone()
      return moment.tz(this.form.recurrenceEndDate, 'YYYY-MM-DD', tz).endOf('day')
    },

    scheduleSummary() {
      if (!this.startMoment || !this.endMoment) {
        return 'Select date, start time, and end time (or use a duration preset).'
      }
      const span = this.formatDuration(this.endMoment.diff(this.startMoment, 'minutes'))
      let summary =
        'Scheduled window ' +
        this.startMoment.format('ddd, MMM D · h:mm A') +
        ' – ' +
        this.endMoment.format('h:mm A') +
        ' (' +
        span +
        ')'
      if (this.showRecurrenceEndDate && this.recurrenceEndMoment) {
        summary +=
          '. Repeats until ' + this.recurrenceEndMoment.format('ddd, MMM D') + '.'
      }
      const pre = parseInt(this.form.pre_roll_seconds, 10) || 0
      if (pre > 0) {
        summary +=
          ' Outputs turn on at ' +
          this.startMoment.clone().subtract(pre, 'seconds').format('h:mm A') +
          ' (pre-roll).'
      }
      return summary
    },
  },

  watch: {
    async value(open) {
      if (open) {
        this.startTimeManual = false
        this.endTimeManual = false
        this.startTimeEditText = ''
        this.endTimeEditText = ''
        this.recurrenceEndDateTouched = false
        if (this.event) {
          this.form = this.eventToForm(this.event)
        } else {
          this.form = this.emptyForm()
          this.applyInitialSchedule(this.initialSchedule)
        }
        this.syncRecurrenceEndDateFromFrequency(false)
        if (!this.form.device_guid && this.defaultDeviceGuid) {
          this.form.device_guid = this.defaultDeviceGuid
        }
        await this.loadDeviceChoices()
        if (this.form.device_guid) {
          this.hydrateDeviceForPicker(this.form.device_guid)
        }
      }
    },
    deviceGuid(newGuid, oldGuid) {
      if (newGuid !== oldGuid && !this.editingEvent && this.value) {
        this.form.output_ids = []
      }
      if (newGuid && newGuid !== oldGuid) {
        this.hydrateDeviceForPicker(newGuid)
      }
    },
    'form.startTime'(val, oldVal) {
      if (this.startTimeManual || val === oldVal) {
        return
      }
      this.applyDurationPreset()
    },
    'form.startDate'() {
      if (!this.startTimeManual) {
        this.applyDurationPreset()
      }
      if (this.showRecurrenceEndDate && !this.recurrenceEndDateTouched) {
        this.syncRecurrenceEndDateFromFrequency(false)
      }
    },
    'form.endTime'(val, oldVal) {
      if (this.endTimeManual || val === oldVal) {
        return
      }
      this.syncDurationFromEnd()
    },
    'form.recurrence.frequency'(freq, oldFreq) {
      if (freq === 'none') {
        this.form.recurrenceEndDate = null
        return
      }
      const frequencyChanged = oldFreq !== freq
      this.syncRecurrenceEndDateFromFrequency(frequencyChanged)
    },
  },

  methods: {
    displayNow() {
      const tz = this.$helpers.getDisplayTimezone()
      return moment.tz(tz)
    },

    roundToNearest15Minutes(momentValue) {
      const m = momentValue.clone().seconds(0).milliseconds(0)
      const roundedMinutes = Math.round(m.minutes() / 15) * 15
      if (roundedMinutes === 60) {
        return m.add(1, 'hour').minutes(0)
      }
      return m.minutes(roundedMinutes)
    },

    defaultScheduleFields() {
      const start = this.roundToNearest15Minutes(this.displayNow())
      const end = start.clone().add(60, 'minutes')
      return {
        startDate: start.format('YYYY-MM-DD'),
        startTime: start.format('HH:mm'),
        endTime: end.format('HH:mm'),
        durationMinutes: 60,
      }
    },

    buildTimeSlotItems(currentValue) {
      const items = []
      const seen = {}
      for (let hour = 0; hour < 24; hour += 1) {
        for (const minute of [0, 15, 30, 45]) {
          const slot = moment().hours(hour).minutes(minute).seconds(0)
          const value = slot.format('HH:mm')
          if (!seen[value]) {
            seen[value] = true
            items.push({
              text: slot.format('h:mm A'),
              value,
            })
          }
        }
      }
      if (currentValue && !seen[currentValue]) {
        const custom = moment(currentValue, 'HH:mm', true)
        items.unshift({
          text: custom.isValid() ? custom.format('h:mm A') : currentValue,
          value: currentValue,
        })
      }
      return items
    },

    formatTimeDisplay(hhmm) {
      if (!hhmm) {
        return ''
      }
      const parsed = moment(hhmm, 'HH:mm', true)
      return parsed.isValid() ? parsed.format('h:mm A') : hhmm
    },

    selectStartTimeSlot(value) {
      this.form.startTime = value
      this.applyDurationPreset()
    },

    selectEndTimeSlot(value) {
      this.form.endTime = value
      this.syncDurationFromEnd()
    },

    parseTimeToHHmm(value) {
      if (!value || typeof value !== 'string') {
        return null
      }
      const trimmed = value.trim()
      const formats = [
        'h:mm A',
        'hh:mm A',
        'h:mma',
        'hh:mma',
        'h:mm a',
        'hh:mm a',
        'H:mm',
        'HH:mm',
        'H:mm:ss',
        'h:mm:ss A',
      ]
      const parsed = moment(trimmed, formats, true)
      if (parsed.isValid()) {
        return parsed.format('HH:mm')
      }
      return null
    },

    enableStartTimeManual() {
      this.startTimeEditText = this.formatTimeDisplay(this.form.startTime)
      this.startTimeManual = true
      this.$nextTick(() => {
        const ref = this.$refs.startTimeManualInput
        if (ref && ref.focus) {
          ref.focus()
        }
      })
    },

    enableEndTimeManual() {
      this.endTimeEditText = this.formatTimeDisplay(this.form.endTime)
      this.endTimeManual = true
      this.$nextTick(() => {
        const ref = this.$refs.endTimeManualInput
        if (ref && ref.focus) {
          ref.focus()
        }
      })
    },

    commitStartTimeManual() {
      const parsed = this.parseTimeToHHmm(this.startTimeEditText)
      if (!parsed) {
        this.$emit('error', 'Enter a valid start time, e.g. 2:30 PM')
        return
      }
      this.form.startTime = parsed
      this.startTimeManual = false
      this.startTimeEditText = ''
      this.syncDurationFromEnd()
    },

    commitEndTimeManual() {
      const parsed = this.parseTimeToHHmm(this.endTimeEditText)
      if (!parsed) {
        this.$emit('error', 'Enter a valid end time, e.g. 4:15 PM')
        return
      }
      this.form.endTime = parsed
      this.endTimeManual = false
      this.endTimeEditText = ''
      this.syncDurationFromEnd()
    },

    applyDurationPreset() {
      if (!this.startMoment || !this.form.durationMinutes) {
        return
      }
      this.form.endTime = this.startMoment
        .clone()
        .add(this.form.durationMinutes, 'minutes')
        .format('HH:mm')
    },

    syncDurationFromEnd() {
      if (!this.startMoment || !this.endMoment) {
        return
      }
      const minutes = this.endMoment.diff(this.startMoment, 'minutes')
      if (minutes >= 1) {
        this.form.durationMinutes = minutes
      }
    },

    applyInitialSchedule(seed) {
      if (!seed || !seed.startDate || !seed.startTime) {
        return
      }
      this.form.startDate = seed.startDate
      this.form.startTime = seed.startTime
      const duration = seed.durationMinutes || 60
      this.form.durationMinutes = duration
      const tz = this.$helpers.getDisplayTimezone()
      const start = moment.tz(
        seed.startDate + ' ' + seed.startTime,
        'YYYY-MM-DD HH:mm',
        tz
      )
      this.form.endTime = start.clone().add(duration, 'minutes').format('HH:mm')
    },

    hydrateDeviceForPicker(deviceGuid) {
      const choice = this.deviceChoices.find((c) => c.value === deviceGuid)
      const attrs = choice && choice.device ? choice.device : false
      this.$devices.getDevice(deviceGuid, attrs)
    },

    async loadDeviceChoices() {
      if (this.scope === 'device') {
        return
      }
      const orgGuid = this.orgGuid
      if (!orgGuid) {
        this.deviceChoices = []
        return
      }
      try {
        const devices = await this.$scheduler._listOrgDevices(orgGuid)
        const choices = await Promise.all(
          devices.map(async (d) => ({
            value: d.device_guid,
            text: await this.$scheduler._resolveDeviceDisplayName(d),
            device: d,
          }))
        )
        this.deviceChoices = choices.sort((a, b) =>
          a.text.localeCompare(b.text)
        )
      } catch (e) {
        console.warn('Failed to load devices for scheduler form', e)
      }
    },

    emptyForm() {
      return {
        device_guid: null,
        name: '',
        location: '',
        ...this.defaultScheduleFields(),
        output_ids: [],
        notes: '',
        pre_roll_seconds: 0,
        post_roll_seconds: 0,
        recurrence: { frequency: 'none', interval: 1, by_weekday: [] },
        recurrenceEndDate: null,
      }
    },

    syncRecurrenceEndDateFromFrequency(resetToDefault) {
      const freq = this.form.recurrence && this.form.recurrence.frequency
      if (!freq || freq === 'none') {
        this.form.recurrenceEndDate = null
        return
      }
      const tz = this.$helpers.getDisplayTimezone()
      if (!resetToDefault && this.recurrenceEndDateTouched && this.form.recurrenceEndDate) {
        return
      }
      const existingUntil =
        this.form.recurrence && this.form.recurrence.until
      if (!resetToDefault && existingUntil) {
        const fromUntil = recurrenceEndDateFromUntil(existingUntil, tz)
        if (fromUntil) {
          this.form.recurrenceEndDate = fromUntil
          return
        }
      }
      if (!this.form.startDate) {
        return
      }
      this.form.recurrenceEndDate = defaultRecurrenceEndDate(
        this.form.startDate,
        freq,
        tz
      )
    },

    eventToForm(evt) {
      const tz = this.$helpers.getDisplayTimezone()
      const start = moment.utc(evt.start_time).tz(tz)
      const end = moment.utc(evt.end_time).tz(tz)
      let durationMinutes = end.diff(start, 'minutes')
      if (durationMinutes < 1) {
        durationMinutes = 1
      }
      const recurrence =
        evt.series_recurrence ||
        evt.recurrence ||
        { frequency: 'none', interval: 1, by_weekday: [] }
      const recurrenceEndDate = recurrenceEndDateFromUntil(
        recurrence.until,
        tz
      )
      if (recurrence.frequency && recurrence.frequency !== 'none' && !recurrenceEndDate) {
        this.recurrenceEndDateTouched = false
      } else if (recurrenceEndDate) {
        this.recurrenceEndDateTouched = true
      }
      return {
        device_guid: evt.device_guid,
        name: evt.name,
        location: evt.location || '',
        startDate: start.format('YYYY-MM-DD'),
        startTime: start.format('HH:mm'),
        endTime: end.format('HH:mm'),
        durationMinutes,
        output_ids: [...(evt.output_ids || [])],
        notes: evt.notes || '',
        pre_roll_seconds: evt.pre_roll_seconds || 0,
        post_roll_seconds: evt.post_roll_seconds || 0,
        recurrence: { ...recurrence },
        recurrenceEndDate,
      }
    },

    formatDuration(minutes) {
      if (!minutes || minutes < 1) {
        return '0 min'
      }
      if (minutes < 60) {
        return minutes + ' min'
      }
      const hours = Math.floor(minutes / 60)
      const remainder = minutes % 60
      if (remainder === 0) {
        return hours === 1 ? '1 hour' : hours + ' hours'
      }
      return hours + ' h ' + remainder + ' min'
    },

    buildRecurrencePayload() {
      const recurrence = { ...this.form.recurrence }
      const freq = recurrence.frequency || 'none'
      if (
        this.editingEvent &&
        this.isSeriesEdit &&
        this.editScope === 'this'
      ) {
        return { frequency: 'none', interval: 1 }
      }
      if (freq === 'none') {
        delete recurrence.until
        delete recurrence.count
        return recurrence
      }
      const tz = this.$helpers.getDisplayTimezone()
      let endDate = this.form.recurrenceEndDate
      if (!endDate) {
        endDate = defaultRecurrenceEndDate(this.form.startDate, freq, tz)
      }
      if (!endDate) {
        return null
      }
      const until = recurrenceUntilFromEndDate(endDate, tz)
      if (!until) {
        return null
      }
      recurrence.until = until
      delete recurrence.count
      if (freq === 'weekly') {
        const weekdays = recurrence.by_weekday || []
        if (!weekdays.length && this.startMoment) {
          recurrence.by_weekday = [this.startMoment.day()]
        }
      }
      return recurrence
    },

    buildPayload() {
      const outputIds = (this.form.output_ids || [])
        .map((id) => parseInt(id, 10))
        .filter((n) => !isNaN(n))
      const start = this.startMoment
      const end = this.endMoment
      const recurrence = this.buildRecurrencePayload()
      return {
        name: this.form.name,
        location: this.form.location,
        start_time: start.utc().format('YYYY-MM-DDTHH:mm:ss.SSS') + 'Z',
        end_time: end.utc().format('YYYY-MM-DDTHH:mm:ss.SSS') + 'Z',
        output_ids: outputIds,
        notes: this.form.notes,
        pre_roll_seconds: this.form.pre_roll_seconds || 0,
        post_roll_seconds: this.form.post_roll_seconds || 0,
        recurrence,
      }
    },

    onSaveClick() {
      if (!this.canEdit) {
        return
      }
      if (this.isSeriesEdit) {
        this.editScope = 'this'
        this.editScopeDialog = true
        return
      }
      this.save()
    },

    async save() {
      if (!this.canEdit) return
      if (!this.deviceGuid) {
        this.$emit('error', 'Select a device')
        return
      }
      if (!this.startMoment || !this.endMoment) {
        this.$emit('error', 'Set a valid date, start time, and end time')
        return
      }
      const windowMinutes = this.endMoment.diff(this.startMoment, 'minutes')
      if (windowMinutes < 1) {
        this.$emit('error', 'End time must be after start time')
        return
      }
      const outputIds = (this.form.output_ids || [])
        .map((id) => parseInt(id, 10))
        .filter((n) => !isNaN(n))
      if (!outputIds.length) {
        this.$emit('error', 'Select at least one output')
        return
      }
      const recurrence = this.buildRecurrencePayload()
      if (!recurrence) {
        this.$emit('error', 'Set a valid series end date for recurring events')
        return
      }
      if (recurrence.frequency && recurrence.frequency !== 'none') {
        if (!recurrence.until) {
          this.$emit('error', 'Series end date is required for recurring events')
          return
        }
        const tz = this.$helpers.getDisplayTimezone()
        const untilM = moment.utc(recurrence.until)
        if (untilM.isBefore(this.startMoment)) {
          this.$emit(
            'error',
            'Series end date must be on or after the event start date'
          )
          return
        }
        if (this.form.recurrenceEndDate && this.form.startDate) {
          const endDay = moment.tz(this.form.recurrenceEndDate, 'YYYY-MM-DD', tz)
          const startDay = moment.tz(this.form.startDate, 'YYYY-MM-DD', tz)
          if (endDay.isBefore(startDay, 'day')) {
            this.$emit(
              'error',
              'Series end date must be on or after the event start date'
            )
            return
          }
        }
      }
      this.saving = true
      try {
        const payload = this.buildPayload()
        let response
        if (this.editingEvent) {
          const scope = this.isSeriesEdit ? this.editScope : 'this'
          response = await this.$scheduler.updateEvent(
            this.deviceGuid,
            this.patchStartTime,
            payload,
            scope
          )
          this.editScopeDialog = false
        } else {
          response = await this.$scheduler.createEvent(this.deviceGuid, payload)
        }
        this.$emit('saved', response)
        this.close()
      } catch (err) {
        console.error(err)
        this.$emit('error', this.$helpers.parseError(err))
      } finally {
        this.saving = false
      }
    },

    close() {
      this.dialogVisible = false
    },
  },
}
</script>
